import { storageService } from "@/services/storageService";
import { toast } from "sonner";
import { toastDuration } from "@/components/ui/sonner";
import { findFilePathByDocumentId, getValidatedFilePath } from "@/utils/documentPathValidator";
import { apiClient } from "@/lib/apiClient";
import {
  usePreviewPanelStore,
  createDocumentPreviewKey,
  PREVIEW_TAB_MAX,
  type PreviewRequestId,
  type PreviewResource,
  type PreviewTabKey,
} from "@/stores/previewPanelStore";
import { ErrorService } from "@/lib/errors/ErrorService";
import type { ApiError } from "@/lib/errors/ErrorService";

// Audio file extensions
const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'wav', 'ogg', 'opus', 'aac', 'flac', 'webm'];

/** Provenance of a piece's extracted text (viewer badge). Null = unknown/legacy. */
export type OcrSource = 'ocr' | 'vision' | 'native' | null;

// Transcription response interface - matches backend format directly
interface TranscriptionResponse {
  content: string;
  documentName: string;
  chunkCount: number;
  createdAt: string;
}

interface SecureDocumentAccess {
  signedUrl: string;
  storagePath: string;
}

export interface DocumentViewerOptions {
  filePath: string;
  fileName: string;
  fileType?: string;
  caseId?: string;
  documentId?: string;
  /** Bibliothèque de modèles : id du pack. Active le toggle « Texte extrait » du panneau,
   *  sourcé depuis GET /template-packs/:id/extracted-text (pas de documentId côté modèle). */
  templatePackId?: string;
  /** Pre-opened window to avoid popup blocker when called after async work */
  preOpenedTab?: Window | null;
}

interface PreviewLoad {
  key: PreviewTabKey;
  requestId: PreviewRequestId;
  options: DocumentViewerOptions;
  fileType: string;
}

interface CachedPreviewUrl {
  resource: PreviewResource & { kind: 'remote-url' };
  fileType: string;
  expiry: number;
}

export class DocumentViewerService {
  /**
   * A pending load is shared only while its tab remains open. A close followed by a reopen
   * deliberately replaces this entry: the old request id must never control the new tab.
   */
  private static inFlightLoads = new Map<PreviewTabKey, Promise<void>>();

  // Remotely hosted signed URLs can safely outlive a tab. Blob URLs cannot: tabs own and revoke them.
  private static urlCache = new Map<string, CachedPreviewUrl>();
  private static readonly CACHE_TTL = 30 * 60 * 1000;
  private static nextPreviewRequestSequence = 0;

  private static createPreviewRequestId(): PreviewRequestId {
    this.nextPreviewRequestSequence += 1;
    return `document-viewer-load-${this.nextPreviewRequestSequence}`;
  }

  private static toPreviewResource(url: string): PreviewResource {
    return { url, kind: url.startsWith('blob:') ? 'object-url' : 'remote-url' };
  }

  private static getCachedUrl(filePath: string): Pick<CachedPreviewUrl, 'resource' | 'fileType'> | null {
    const entry = this.urlCache.get(filePath);
    if (!entry) return null;
    if (Date.now() > entry.expiry) {
      this.urlCache.delete(filePath);
      return null;
    }
    return { resource: entry.resource, fileType: entry.fileType };
  }

  private static setCachedUrl(filePath: string, resource: PreviewResource, fileType: string): void {
    // Object URLs are owned by one tab and revoked on close. Caching them would let a later
    // tab reopen a dead blob URL; only remotely hosted signed URLs survive the tab lifecycle.
    if (resource.kind !== 'remote-url') return;
    this.urlCache.set(filePath, {
      resource: { url: resource.url, kind: 'remote-url' },
      fileType,
      expiry: Date.now() + this.CACHE_TTL,
    });
  }
  
  /**
   * Get the actual file type from filename and MIME type
   */
  /** Mappe une extension de fichier vers un type de preview, ou null si inconnue. */
  private static extToType(ext?: string): string | null {
    if (!ext) return null;
    switch (ext) {
      case 'pdf': return 'pdf';
      case 'docx': return 'docx';
      case 'doc': return 'doc';
      case 'png':
      case 'jpg':
      case 'jpeg': return 'image';
      case 'eml': return 'eml';
      case 'msg': return 'msg';
      case 'csv': return 'csv';
      case 'xlsx':
      case 'xls': return 'spreadsheet';
      default:
        return AUDIO_EXTENSIONS.includes(ext) ? 'audio' : null;
    }
  }

  private static getFileType(fileName: string, mimeType?: string, filePath?: string): string {
    // Priority 1: extension. ATTENTION — beaucoup de pièces ont un nom d'affichage SANS
    // extension (ex: « Acte de vente du 22/03/2010 ») alors que le `file_path` porte la
    // vraie extension (.docx/.msg/.pdf…). On détecte donc sur `file_path` EN PRIORITÉ,
    // puis sur le nom, avant de retomber sur le mimeType. Sans ça, `canPreviewDocument`
    // renvoyait false pour tout sauf le PDF → le bouton « œil » disparaissait sur les .docx.
    const pathExt = filePath?.split('?')[0]?.split('.').pop()?.toLowerCase();
    const nameExt = fileName?.split('.').pop()?.toLowerCase();
    const byExt = this.extToType(pathExt) ?? this.extToType(nameExt);
    if (byExt) return byExt;

    // Priority 2: Check if mimeType is actually a file extension (inconsistent storage)
    if (mimeType) {
      const mimeTypeLower = mimeType.toLowerCase();
      
      // Handle cases where document_type stores just the extension
      if (mimeTypeLower === 'pdf') return 'pdf';
      if (mimeTypeLower === 'docx') return 'docx';
      if (mimeTypeLower === 'doc') return 'doc';
      if (['png', 'jpg', 'jpeg'].includes(mimeTypeLower)) return 'image';
      if (mimeTypeLower === 'eml') return 'eml';
      if (mimeTypeLower === 'msg') return 'msg';
      if (mimeTypeLower === 'csv') return 'csv';
      if (['xlsx', 'xls'].includes(mimeTypeLower)) return 'spreadsheet';
      
      // Handle proper MIME types
      if (mimeTypeLower.includes('pdf')) return 'pdf';
      if (mimeTypeLower.includes('wordprocessingml')) return 'docx';
      if (mimeTypeLower.includes('msword')) return 'doc';
      if (mimeTypeLower.includes('image')) return 'image';
      if (mimeTypeLower.startsWith('audio/')) return 'audio';
      if (mimeTypeLower.includes('spreadsheetml') || mimeTypeLower.includes('ms-excel')) return 'spreadsheet';
    }

    return 'unknown';
  }

  /**
   * Check if a document can be previewed
   */
  static canPreviewDocument(fileName: string, mimeType?: string, filePath?: string): boolean {
    const fileType = this.getFileType(fileName, mimeType, filePath);
    return ['pdf', 'docx', 'doc', 'audio', 'eml', 'msg', 'csv', 'spreadsheet', 'image'].includes(fileType);
  }

  /**
   * Check if a document can be opened in Microsoft Word Desktop.
   * Supports Word documents (docx, doc) and PDFs (Word converts them natively).
   */
  static canOpenInWord(fileName: string, mimeType?: string, filePath?: string): boolean {
    const fileType = this.getFileType(fileName, mimeType, filePath);
    return ['pdf', 'docx', 'doc'].includes(fileType);
  }

  /**
   * Open a document in Microsoft Word Desktop using the `ms-word:` URI protocol.
   *
   * - DOCX/DOC: uses `nft` (new-from-template) which opens an editable copy
   *   directly in Word, bypassing Protected View.
   * - PDF on macOS: uses `ofe` (open-for-edit); Word converts the PDF natively.
   * - PDF on Windows: the protocol handler does not support PDFs, so the file
   *   is downloaded with a toast guiding the user to open it manually in Word.
   *
   * @see https://learn.microsoft.com/en-us/office/client-developer/office-uri-schemes
   */
  static async openInWord(options: DocumentViewerOptions): Promise<void> {
    const { filePath, fileName, fileType: mimeType, caseId, documentId } = options;
    const fileType = this.getFileType(fileName, mimeType, filePath);

    if (!['pdf', 'docx', 'doc'].includes(fileType)) {
      toast.error("Format non supporté", {
        description: "Seuls les fichiers Word (.docx, .doc) et PDF peuvent être ouverts dans Word"
      });
      return;
    }

    try {
      const loadingToastId = toast.loading('Préparation du document…');

      const documentAccess = await this.getSecureDocumentAccess(filePath, fileName, caseId, documentId);

      toast.dismiss(loadingToastId);

      const isPdf = fileType === 'pdf';
      const isWindows = /Windows/i.test(navigator.userAgent);

      if (isPdf && isWindows) {
        await storageService.triggerDownload(documentAccess.storagePath, fileName);
        toast.info('PDF téléchargé', {
          description: 'Faites un clic-droit sur le fichier téléchargé → « Ouvrir avec » → Microsoft Word pour le convertir en document éditable.',
          ...toastDuration(10_000),
        });
        return;
      }

      // nft creates an editable copy (no Protected View / read-only).
      // ofe is needed for PDFs since nft may not trigger conversion.
      const command = isPdf ? 'ofe' : 'nft';

      const anchor = document.createElement('a');
      anchor.href = `ms-word:${command}|u|${documentAccess.signedUrl}`;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      if (isPdf) {
        toast.success('Ouverture dans Word…', {
          description: 'Word va convertir le PDF en document éditable.',
          ...toastDuration(6000),
        });
      } else {
        toast.success('Document ouvert dans Word', {
          description: 'Une copie éditable a été créée dans Word.'
        });
      }
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'open_in_word', document_id: options.documentId } });
    }
  }

  /**
   * Check if a file is an audio file
   */
  static isAudioFile(fileName: string): boolean {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return AUDIO_EXTENSIONS.includes(extension || '');
  }

  /**
   * Fetch transcription for an audio document
   */
  static async getTranscription(documentId: string): Promise<TranscriptionResponse | null> {
    console.log('[getTranscription] Fetching transcription for documentId:', documentId);
    try {
      const response = await apiClient.get<TranscriptionResponse>(`/documents/${documentId}/transcription`);
      console.log('[getTranscription] Raw API response:', response);
      console.log('[getTranscription] Response type:', typeof response);
      console.log('[getTranscription] Has content?:', !!(response as any)?.content);
      return response;
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'get_transcription', document_id: documentId }, silent: true });
      return null;
    }
  }

  /**
   * Fetch the clean OCR markdown extracted from a piece (viewer « texte extrait »).
   * Returns null when no extracted text exists (piece synthesized before the feature,
   * migration 124) or on error — the caller then disables/greys the toggle.
   */
  static async getOcrText(
    documentId: string,
  ): Promise<{ content: string; source: OcrSource; documentName?: string } | null> {
    try {
      // apiClient.get UNWRAPS the response envelope: `return result.data || result`
      // (apiClient.ts). So on a HIT the route's `data:{content,…}` is returned
      // directly as the inner object; on a MISS `data:null` makes apiClient fall
      // back to the full envelope `{success, exists:false, data:null}`, which simply
      // has no `content`. Detect success by the presence of `content` — NOT by an
      // `exists` flag, which does not survive the unwrap (that was the bug: the poll
      // read `response.exists` === undefined → always null → spinner never resolved).
      const res = await apiClient.get<{
        content?: string;
        source?: OcrSource;
        documentName?: string;
      }>(`/documents/${documentId}/ocr-text`);
      if (res?.content) {
        return {
          content: res.content,
          source: res.source ?? null,
          documentName: res.documentName,
        };
      }
      return null;
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'get_ocr_text', document_id: documentId }, silent: true });
      return null;
    }
  }

  /**
   * Fetch the extracted text of a Bibliothèque de modèles pack (« Texte extrait » toggle).
   *
   * Mirrors {@link getOcrText} but hits GET /template-packs/:id/extracted-text. The text is
   * extracted on demand server-side (born-digital .docx/.pdf, no OCR) — no job/polling. Same
   * envelope-unwrap caveat as getOcrText: success is the presence of `content`, never `exists`
   * (which does not survive apiClient's `result.data || result` unwrap).
   */
  static async getTemplatePackText(
    templatePackId: string,
  ): Promise<{ content: string; source: OcrSource; documentName?: string } | null> {
    try {
      const res = await apiClient.get<{
        content?: string;
        source?: OcrSource;
        documentName?: string;
      }>(`/template-packs/${templatePackId}/extracted-text`);
      if (res?.content) {
        return {
          content: res.content,
          source: res.source ?? null,
          documentName: res.documentName,
        };
      }
      return null;
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'get_template_pack_text', template_pack_id: templatePackId }, silent: true });
      return null;
    }
  }

  /**
   * Trigger on-demand OCR extraction for a piece that has no stored extracted text yet
   * (lazy backfill for pieces synthesized before the feature shipped). Enqueues an
   * `ocr-extract` background job; the caller then polls {@link getOcrText} until it appears.
   * Returns true when extraction is enqueued OR the text already exists, false on error.
   */
  static async triggerOcrExtract(documentId: string): Promise<boolean> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        enqueued?: boolean;
        alreadyExists?: boolean;
      }>(`/documents/${documentId}/ocr-text/extract`, {});
      return !!response?.success;
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'trigger_ocr_extract', document_id: documentId }, silent: true });
      return false;
    }
  }

  /**
   * Display audio transcription in a new tab
   */
  private static async viewAudioTranscription(documentId: string, fileName: string): Promise<void> {
    console.log('[viewAudioTranscription] Starting with documentId:', documentId, 'fileName:', fileName);
    toast.info(`Récupération de la transcription...`);
    const result = await this.getTranscription(documentId);
    
    console.log('[viewAudioTranscription] Got result:', result);
    console.log('[viewAudioTranscription] result is truthy?:', !!result);
    console.log('[viewAudioTranscription] result.content exists?:', !!(result as any)?.content);
    console.log('[viewAudioTranscription] result keys:', result ? Object.keys(result) : 'null');

    if (result && result.content) {
      console.log('[viewAudioTranscription] Condition passed, opening window...');
      const transcriptionWindow = window.open('', '_blank', 'noopener,noreferrer');
      if (transcriptionWindow) {
        transcriptionWindow.document.write(`
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Transcription - ${fileName}</title>
  <style>
    body { font-family: system-ui, sans-serif; line-height: 1.6; padding: 40px; max-width: 800px; margin: auto; background: #f8f9fa; color: #333; }
    h1 { color: #0056b3; border-bottom: 2px solid #dee2e6; padding-bottom: 15px; }
    pre { background: #fff; padding: 20px; border-radius: 8px; border: 1px solid #dee2e6; white-space: pre-wrap; word-wrap: break-word; }
    .meta { font-size: 0.85em; color: #6c757d; margin-top: 20px; padding-top: 15px; border-top: 1px solid #dee2e6; }
  </style>
</head>
<body>
  <h1>📝 Transcription: ${fileName}</h1>
  <pre>${result.content}</pre>
  <div class="meta">
    <p>Créé le: ${new Date(result.createdAt).toLocaleString('fr-FR')}</p>
  </div>
</body>
</html>`);
        transcriptionWindow.document.close();
        toast.success('Transcription ouverte');
      } else {
        toast.error('Popup bloquée par le navigateur');
      }
    } else {
      toast.error('Transcription non disponible');
    }
  }

  /**
   * Get a secure URL for document viewing with automatic path correction
   */
  private static isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private static isApiError(error: unknown): error is ApiError {
    return error instanceof Error && (
      'statusCode' in error ||
      'errorCode' in error ||
      'requestId' in error ||
      'details' in error
    );
  }

  private static buildDocumentUrlError(error: unknown, filePath: string, fileName?: string): Error {
    const documentLabel = fileName ? `"${fileName}"` : 'document sans nom';
    const wrapped = new Error(
      `DocumentViewerService.getSecureDocumentUrl failed for ${documentLabel} at storage path "${filePath}". Check case_documents.file_path and /storage/documents/signed-url response.`,
    ) as ApiError & { cause?: unknown };

    // Preserve the original ApiError fields so ErrorService keeps the real
    // HTTP status, backend code and requestId in PostHog instead of reporting
    // this as an unrelated generic frontend crash.
    wrapped.cause = error;
    if (this.isApiError(error)) {
      if (typeof error.statusCode === 'number') wrapped.statusCode = error.statusCode;
      if (typeof error.errorCode === 'string') wrapped.errorCode = error.errorCode;
      if (typeof error.requestId === 'string') wrapped.requestId = error.requestId;
      if (this.isRecord(error.details)) wrapped.details = error.details;
    }

    return wrapped;
  }

  private static async getSecureDocumentAccess(filePath: string, fileName?: string, caseId?: string, documentId?: string): Promise<SecureDocumentAccess> {
    console.log('🔐 [getSecureDocumentUrl] Starting with:', { filePath, fileName, caseId, documentId });
    let cleanPath = filePath;
    
    try {
      // More comprehensive path cleaning
      console.log('🧹 [getSecureDocumentUrl] Initial path:', cleanPath);
      
      // Remove any duplicate "documents/" prefixes
      if (cleanPath.includes('documents/documents/')) {
        cleanPath = cleanPath.replace(/documents\/documents\//g, 'documents/');
        console.log('🧹 [getSecureDocumentUrl] After removing duplicate documents/:', cleanPath);
      }
      
      // Ensure we don't have leading slashes
      cleanPath = cleanPath.replace(/^\/+/, '');
      console.log('🧹 [getSecureDocumentUrl] After removing leading slashes:', cleanPath);
      
      // If we have a fileName, try path validation with fallback
      if (fileName && caseId) {
        console.log('🔍 [getSecureDocumentUrl] Calling getValidatedFilePath...');
        const validatedPath = await getValidatedFilePath(cleanPath, fileName, caseId, documentId);
        if (validatedPath) {
          console.log('✅ [getSecureDocumentUrl] Got validated path:', validatedPath);
          cleanPath = validatedPath;
        } else {
          console.warn('⚠️ [getSecureDocumentUrl] No validated path returned, using cleaned path:', cleanPath);
        }
      } else {
        console.log('⚠️ [getSecureDocumentUrl] Missing fileName or caseId, skipping validation');
      }
      
      // Use storageService to get signed URL (1 hour expiry, inline display)
      console.log('🔗 [getSecureDocumentUrl] Calling storageService.getCachedSignedUrl with path:', cleanPath);
      const signedUrl = await storageService.getCachedSignedUrl(cleanPath, { download: false });
      console.log('✅ [getSecureDocumentUrl] Got signed URL:', signedUrl.substring(0, 100) + '...');
      return { signedUrl, storagePath: cleanPath };
    } catch (error) {
      throw this.buildDocumentUrlError(error, cleanPath, fileName);
    }
  }

  private static async getSecureDocumentUrl(filePath: string, fileName?: string, caseId?: string, documentId?: string): Promise<string> {
    const { signedUrl } = await this.getSecureDocumentAccess(filePath, fileName, caseId, documentId);
    return signedUrl;
  }

  /**
   * Get inline blob URL via backend proxy. No CORS issues, no Content-Disposition problems.
   * Falls back to S3 signed URL on proxy failure.
   */
  private static async getInlineBlobUrl(filePath: string, mimeType: string, fileName?: string, caseId?: string, documentId?: string): Promise<string> {
    try {
      return await storageService.getInlineBlobUrl(filePath, mimeType);
    } catch (proxyError) {
      console.warn('[getInlineBlobUrl] Backend proxy failed, falling back to signed URL:', proxyError);
      const signedUrl = await this.getSecureDocumentUrl(filePath, fileName, caseId, documentId);
      return signedUrl;
    }
  }

  /**
   * View a PDF document in browser's native viewer (new tab).
   * Uses backend proxy for inline display (avoids CORS + Content-Disposition issues).
   */
  /**
   * View a DOCX/DOC document using Google Docs Viewer (new tab)
   * 🔧 FIX: Use Google Docs viewer which is more reliable than Office Online viewer
   * This fixes the download issue by properly rendering the document
   */
  /**
   * Télécharge une pièce et l'enregistre sous `fileName`.
   *
   * `fileName` est un nom de présentation (« Pièce n°3 - Contrat.pdf »,
   * « 3 Contrat.pdf ») : il n'identifie jamais le fichier. Le chemin se résout
   * par `documentId` quand l'appelant le connaît, sinon c'est `filePath` tel
   * quel. La résolution par nom envoyait le fichier d'un homonyme, par exemple
   * une pièce redéposée sous son nom de téléchargement (#1706).
   */
  static async downloadDocument(
    filePath: string,
    fileName: string,
    caseId?: string,
    documentId?: string,
    onStart?: () => void,
    onComplete?: () => void
  ): Promise<void> {
    try {
      onStart?.();

      const storagePath = caseId && documentId
        ? (await findFilePathByDocumentId(caseId, documentId)) ?? filePath
        : filePath;

      await storageService.triggerDownload(storagePath, fileName);

      toast.success('Téléchargement démarré');
    } catch (error) {
      ErrorService.handle(error, {
        context: { action: 'download_document', file_path: filePath, document_id: documentId },
      });
    } finally {
      onComplete?.();
    }
  }

  private static async fetchFileAsText(filePath: string): Promise<string> {
    const blobUrl = await storageService.getInlineBlobUrl(filePath, 'text/plain');
    try {
      const response = await fetch(blobUrl);
      return await response.text();
    } finally {
      // This helper consumes its blob immediately, unlike preview tabs which own their
      // resource until close. Do not leave a failed text conversion holding memory.
      if (blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    }
  }

  private static createHtmlBlobUrl(html: string): string {
    const blob = new Blob([html], { type: 'text/html' });
    return URL.createObjectURL(blob);
  }

  private static escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  private static decodeQuotedPrintable(str: string): string {
    const cleaned = str.replace(/=\r?\n/g, '');
    const bytes: number[] = [];
    let i = 0;
    while (i < cleaned.length) {
      if (cleaned[i] === '=' && i + 2 < cleaned.length) {
        const hex = cleaned.substring(i + 1, i + 3);
        if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
          bytes.push(parseInt(hex, 16));
          i += 3;
          continue;
        }
      }
      bytes.push(cleaned.charCodeAt(i));
      i++;
    }
    try {
      return new TextDecoder('utf-8').decode(new Uint8Array(bytes));
    } catch {
      return cleaned;
    }
  }

  private static decodeMimeEncodedWords(str: string): string {
    return str.replace(/=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g, (_match, charset, encoding, text) => {
      try {
        if (encoding.toUpperCase() === 'Q') {
          const withSpaces = text.replace(/_/g, ' ');
          const bytes: number[] = [];
          let i = 0;
          while (i < withSpaces.length) {
            if (withSpaces[i] === '=' && i + 2 < withSpaces.length && /^[0-9A-Fa-f]{2}$/.test(withSpaces.substring(i + 1, i + 3))) {
              bytes.push(parseInt(withSpaces.substring(i + 1, i + 3), 16));
              i += 3;
            } else {
              bytes.push(withSpaces.charCodeAt(i));
              i++;
            }
          }
          return new TextDecoder(charset.toLowerCase()).decode(new Uint8Array(bytes));
        } else if (encoding.toUpperCase() === 'B') {
          const decoded = atob(text);
          const bytes = new Uint8Array(decoded.length);
          for (let j = 0; j < decoded.length; j++) bytes[j] = decoded.charCodeAt(j);
          return new TextDecoder(charset.toLowerCase()).decode(bytes);
        }
      } catch { /* fallthrough */ }
      return _match;
    });
  }

  private static renderCsvAsHtml(content: string, fileName: string): string {
    const lines = content.split(/\r?\n/).filter(l => l.trim());
    if (lines.length === 0) return '<html><body><p>Fichier CSV vide</p></body></html>';

    const firstLine = lines[0] ?? '';
    const separators = [';', ',', '\t'];
    let separator = ',';
    let maxCount = 0;
    for (const sep of separators) {
      const count = (firstLine.match(new RegExp(sep === '\t' ? '\\t' : sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
      if (count > maxCount) { maxCount = count; separator = sep; }
    }

    const parseRow = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') { inQuotes = !inQuotes; }
        else if (char === separator && !inQuotes) { result.push(current.trim()); current = ''; }
        else { current += char; }
      }
      result.push(current.trim());
      return result;
    };

    const rows = lines.map(parseRow);
    const headerRow = rows[0] ?? [];
    const dataRows = rows.slice(1);

    const thHtml = headerRow.map(h => `<th>${this.escapeHtml(h)}</th>`).join('');
    const tbHtml = dataRows.map(row =>
      `<tr>${row.map(cell => `<td>${this.escapeHtml(cell)}</td>`).join('')}</tr>`
    ).join('');

    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
body{font-family:system-ui,sans-serif;margin:0;padding:16px;color:#333;background:#fff}
h2{font-size:14px;color:#555;margin-bottom:8px;font-weight:500}
.info{font-size:12px;color:#888;margin-bottom:12px}
table{border-collapse:collapse;width:100%;font-size:13px}
th{background:#f1f3f5;font-weight:600;text-align:left;padding:10px 12px;border:1px solid #dee2e6;position:sticky;top:0}
td{padding:8px 12px;border:1px solid #dee2e6}
tr:hover td{background:#f8f9fa}
</style></head><body>
<h2>${this.escapeHtml(fileName)}</h2>
<div class="info">${dataRows.length} ligne${dataRows.length > 1 ? 's' : ''} &middot; ${headerRow.length} colonne${headerRow.length > 1 ? 's' : ''}</div>
<table><thead><tr>${thHtml}</tr></thead><tbody>${tbHtml}</tbody></table>
</body></html>`;
  }

  private static renderEmlAsHtml(content: string): string {
    const headerEnd = content.indexOf('\r\n\r\n') !== -1
      ? content.indexOf('\r\n\r\n')
      : content.indexOf('\n\n');

    const headerSection = headerEnd > 0 ? content.substring(0, headerEnd) : '';
    const bodySection = headerEnd > 0 ? content.substring(headerEnd).trim() : content;

    // Parse headers with continuation line support + MIME encoded-word decoding
    const getHeader = (name: string) => {
      const regex = new RegExp(`^${name}:\\s*(.+(?:\\r?\\n[ \\t]+.+)*)`, 'im');
      const match = headerSection.match(regex);
      if (!match) return '';
      const raw = (match[1] ?? '').replace(/\r?\n[ \t]+/g, ' ').trim();
      return this.decodeMimeEncodedWords(raw);
    };

    const from = getHeader('From');
    const to = getHeader('To');
    const subject = getHeader('Subject');
    const date = getHeader('Date');
    const cc = getHeader('Cc');
    const contentTypeHeader = getHeader('Content-Type');
    const topLevelEncoding = getHeader('Content-Transfer-Encoding')?.toLowerCase() || '';

    let displayBody = bodySection;
    let isHtmlContent = false;

    if (contentTypeHeader.includes('multipart/')) {
      const boundaryMatch = contentTypeHeader.match(/boundary="?([^";\r\n]+)"?/);
      if (boundaryMatch) {
        const boundary = (boundaryMatch[1] ?? '').replace(/"/g, '').trim();
        const escapedBoundary = boundary.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const parts = bodySection.split(new RegExp(`--${escapedBoundary}`));

        let foundHtml = '';
        let foundPlain = '';

        for (const part of parts) {
          if (part.trim() === '--' || part.trim() === '') continue;

          const sepIdx = part.search(/\r?\n\r?\n/);
          if (sepIdx < 0) continue;

          const partHeaders = part.substring(0, sepIdx);
          const partHeadersLower = partHeaders.toLowerCase();
          let partBody = part.substring(sepIdx).replace(/^\r?\n\r?\n/, '').trim();

          if (partHeadersLower.includes('multipart/')) continue;

          // Decode content-transfer-encoding
          if (partHeadersLower.includes('quoted-printable')) {
            partBody = this.decodeQuotedPrintable(partBody);
          } else if (partHeadersLower.includes('base64')) {
            try {
              const decoded = atob(partBody.replace(/\s/g, ''));
              const bytes = new Uint8Array(decoded.length);
              for (let i = 0; i < decoded.length; i++) bytes[i] = decoded.charCodeAt(i);
              partBody = new TextDecoder('utf-8').decode(bytes);
            } catch { /* keep original */ }
          }

          if (partHeadersLower.includes('text/html') && !foundHtml) {
            foundHtml = partBody;
          } else if (partHeadersLower.includes('text/plain') && !foundPlain) {
            foundPlain = partBody;
          }
        }

        if (foundHtml) { displayBody = foundHtml; isHtmlContent = true; }
        else if (foundPlain) { displayBody = foundPlain; isHtmlContent = false; }
      }
    } else {
      // Single-part email: decode transfer encoding
      if (topLevelEncoding.includes('quoted-printable')) {
        displayBody = this.decodeQuotedPrintable(bodySection);
      } else if (topLevelEncoding.includes('base64')) {
        try {
          const decoded = atob(bodySection.replace(/\s/g, ''));
          const bytes = new Uint8Array(decoded.length);
          for (let i = 0; i < decoded.length; i++) bytes[i] = decoded.charCodeAt(i);
          displayBody = new TextDecoder('utf-8').decode(bytes);
        } catch { /* keep original */ }
      }
      isHtmlContent = contentTypeHeader.includes('text/html');
    }

    if (!isHtmlContent) {
      isHtmlContent = displayBody.includes('<html') || displayBody.includes('<body') || displayBody.includes('<table');
    }

    const bodyHtml = isHtmlContent
      ? displayBody
      : `<pre style="white-space:pre-wrap;word-wrap:break-word;font-family:system-ui,sans-serif;font-size:14px;margin:0">${this.escapeHtml(displayBody)}</pre>`;

    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
body{font-family:system-ui,sans-serif;margin:0;padding:24px;color:#333;background:#f8f9fa}
.hdr{background:#fff;border:1px solid #dee2e6;border-radius:8px;padding:20px;margin-bottom:16px}
.subj{font-size:18px;font-weight:600;color:#1a1a1a;margin-bottom:12px}
.fld{margin-bottom:6px;font-size:14px}
.fld .lbl{font-weight:600;color:#555;min-width:50px;display:inline-block}
.bdy{background:#fff;border:1px solid #dee2e6;border-radius:8px;padding:20px;overflow:auto}
</style></head><body>
<div class="hdr">
  <div class="subj">${this.escapeHtml(subject || '(Sans objet)')}</div>
  <div class="fld"><span class="lbl">De :</span> ${this.escapeHtml(from)}</div>
  <div class="fld"><span class="lbl">À :</span> ${this.escapeHtml(to)}</div>
  ${cc ? `<div class="fld"><span class="lbl">Cc :</span> ${this.escapeHtml(cc)}</div>` : ''}
  ${date ? `<div class="fld"><span class="lbl">Date :</span> ${this.escapeHtml(date)}</div>` : ''}
</div>
<div class="bdy">${bodyHtml}</div>
</body></html>`;
  }

  private static closePreOpenedTab(preOpenedTab?: Window | null): void {
    if (preOpenedTab) try { preOpenedTab.close(); } catch (_) { /* Browser-owned window may already be closed. */ }
  }

  private static previewInput(options: DocumentViewerOptions, fileType: string) {
    return {
      caseId: options.caseId ?? 'legacy-preview',
      documentId: options.documentId,
      templatePackId: options.templatePackId,
      fileName: options.fileName,
      filePath: options.filePath,
      fileType,
    };
  }

  private static openPreview(options: DocumentViewerOptions, fileType: string): { load: PreviewLoad | null; key: PreviewTabKey } | null {
    const input = this.previewInput(options, fileType);
    const key = createDocumentPreviewKey(input);
    const store = usePreviewPanelStore.getState();

    if (store.tabsById[key]) {
      store.activateTab(key);
      return { load: null, key };
    }

    const requestId = this.createPreviewRequestId();
    const openedKey = store.openDocument(input, requestId);
    if (!openedKey) {
      toast.info('Limite de 10 aperçus atteinte', {
        description: 'Fermez un onglet avant de prévisualiser une autre pièce.',
      });
      return null;
    }
    return {
      key: openedKey,
      load: { key: openedKey, requestId, options, fileType },
    };
  }

  private static startPreviewLoad(load: PreviewLoad): Promise<void> {
    // Callers only reach this method after creating a tab. Calls which activate an existing
    // tab read `inFlightLoads` directly, while close→reopen replaces the old entry here.
    const promise = this.loadPreview(load);
    this.inFlightLoads.set(load.key, promise);
    void promise.finally(() => {
      if (this.inFlightLoads.get(load.key) === promise) {
        this.inFlightLoads.delete(load.key);
      }
    });
    return promise;
  }

  private static async loadPreview({ key, requestId, options, fileType }: PreviewLoad): Promise<void> {
    const { filePath, fileName, caseId, documentId } = options;
    const store = usePreviewPanelStore.getState();
    const cached = this.getCachedUrl(filePath);
    if (cached) {
      store.resolveLoad(key, requestId, cached.resource, cached.fileType);
      return;
    }

    let effectiveFileType = fileType;

    try {
      let url: string;

      switch (fileType) {
        case 'pdf':
          console.log('[DocumentViewerService] Loading PDF for side panel...');
          url = await this.getInlineBlobUrl(filePath, 'application/pdf', fileName, caseId, documentId);
          break;

        case 'docx':
        case 'doc': {
          // Conversion serveur docx→PDF (LibreOffice + cache content-hash OVH).
          // On bascule fileType en 'pdf' pour que le panel route vers le viewer PDF
          // natif du navigateur — bien plus fiable que `docx-preview` côté client,
          // qui échouait sur les structures Word non triviales (tables imbriquées,
          // numérotation auto, styles complexes — typique d'un bordereau).
          console.log('[DocumentViewerService] Converting DOCX→PDF server-side...');
          url = await storageService.getConvertedPdfBlobUrl(filePath);
          effectiveFileType = 'pdf';
          break;
        }

        case 'spreadsheet': {
          console.log('[DocumentViewerService] Loading via Google Docs Viewer...');
          const documentUrl = await this.getSecureDocumentUrl(filePath, fileName, caseId, documentId);
          url = `https://docs.google.com/viewer?url=${encodeURIComponent(documentUrl)}&embedded=true`;
          break;
        }

        case 'eml':
        case 'msg': {
          // Conversion serveur email→PDF (EmailConverterService : mailparser/.msgreader →
          // wkhtmltopdf, + cache content-hash OVH), même chemin robuste que docx→PDF. On
          // bascule fileType en 'pdf' pour router vers le viewer PDF natif du navigateur —
          // rendu fidèle des emails HTML riches + listing des pièces jointes, et seul moyen
          // de prévisualiser un .msg Outlook (aucun parser client).
          console.log('[DocumentViewerService] Converting email→PDF server-side...');
          try {
            url = await storageService.getConvertedPdfBlobUrl(filePath);
            effectiveFileType = 'pdf';
          } catch (convErr) {
            // Fallback .eml uniquement : rendu HTML client (parser maison) si la conversion
            // serveur échoue (ex: wkhtmltopdf absent en dev local). .msg n'a pas de parser
            // client → on propage l'erreur au catch externe (toast + état d'erreur).
            if (fileType !== 'eml') throw convErr;
            console.warn('[DocumentViewerService] Email→PDF server conversion failed, falling back to client HTML render:', convErr);
            const emlContent = await this.fetchFileAsText(filePath);
            url = this.createHtmlBlobUrl(this.renderEmlAsHtml(emlContent));
          }
          break;
        }

        case 'image': {
          console.log('[DocumentViewerService] Loading image for side panel...');
          const ext = fileName.split('.').pop()?.toLowerCase() || 'png';
          const mimeMap: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' };
          url = await this.getInlineBlobUrl(filePath, mimeMap[ext] || 'image/png', fileName, caseId, documentId);
          break;
        }

        case 'csv': {
          console.log('[DocumentViewerService] Loading CSV for side panel...');
          const csvContent = await this.fetchFileAsText(filePath);
          const csvHtml = this.renderCsvAsHtml(csvContent, fileName);
          url = this.createHtmlBlobUrl(csvHtml);
          break;
        }

        default:
          store.rejectLoad(key, requestId, 'Type de fichier non supporté');
          return;
      }

      const resource = this.toPreviewResource(url);
      // Cache with effective fileType (e.g. 'pdf' after DOCX conversion) for correct re-open.
      this.setCachedUrl(filePath, resource, effectiveFileType);
      store.resolveLoad(key, requestId, resource, effectiveFileType);
      console.log('[DocumentViewerService] Document loaded and cached as:', effectiveFileType);
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'preview_document', document_id: documentId }, silent: true });
      store.rejectLoad(key, requestId, 'Impossible de charger le document');
    }
  }

  private static async handleNonPreviewableDocument(options: DocumentViewerOptions, fileType: string): Promise<void> {
    if (fileType === 'audio') {
      if (!options.documentId) {
        toast.error('ID document manquant pour la transcription');
        return;
      }
      await this.viewAudioTranscription(options.documentId, options.fileName);
      return;
    }

    toast.info('Type de fichier non supporté pour la prévisualisation. Téléchargement...');
    await this.downloadDocument(options.filePath, options.fileName, options.caseId, options.documentId);
  }

  /**
   * Ouvre en aperçu un PDF que l'appelant fabrique lui-même, hors de toute pièce stockée.
   *
   * Premier usage (2026-10-14) : le papier à en-tête d'un en-tête du cabinet (Personnalisation),
   * un Word au corps vidé que l'on convertit en PDF pour que l'avocat voie son en-tête et son pied.
   * Aucun chemin S3 ne s'y prête : la porte de conversion des pièces n'accepte que les préfixes des
   * dossiers et de la bibliothèque. L'onglet est identifié par `viewKey`. Le rouvrir REFAIT le PDF :
   * un modèle remplacé sous le même nom de fichier montrerait sinon l'ancien.
   *
   * @param charger — fabrique le PDF ; son échec s'affiche dans l'onglet, avec `messageDEchec`.
   */
  static async viewGeneratedPdf(options: {
    readonly viewKey: string;
    readonly fileName: string;
    readonly charger: () => Promise<Blob>;
    readonly messageDEchec: string;
  }): Promise<void> {
    const input = { caseId: 'apercu-genere', viewKey: options.viewKey, fileName: options.fileName, fileType: 'pdf' };
    const key = createDocumentPreviewKey(input);
    if (usePreviewPanelStore.getState().tabsById[key]) usePreviewPanelStore.getState().closeTab(key);
    const requestId = this.createPreviewRequestId();
    const openedKey = usePreviewPanelStore.getState().openDocument(input, requestId);
    if (!openedKey) {
      toast.info('Limite de 10 aperçus atteinte', {
        description: 'Fermez un onglet avant d’en ouvrir un autre.',
      });
      return;
    }
    try {
      const pdf = await options.charger();
      usePreviewPanelStore.getState().resolveLoad(openedKey, requestId, this.toPreviewResource(URL.createObjectURL(pdf)), 'pdf');
    } catch (error) {
      console.error('[DocumentViewerService] aperçu généré impossible', { viewKey: options.viewKey, error });
      usePreviewPanelStore.getState().rejectLoad(openedKey, requestId, options.messageDEchec);
    }
  }

  /** Opens or activates one preview tab while retaining audio and download fallbacks. */
  static async viewDocument(options: DocumentViewerOptions): Promise<void> {
    this.closePreOpenedTab(options.preOpenedTab);
    const actualFileType = this.getFileType(options.fileName, options.fileType, options.filePath);

    if (actualFileType === 'audio' || actualFileType === 'unknown') {
      await this.handleNonPreviewableDocument(options, actualFileType);
      return;
    }

    const preview = this.openPreview(options, actualFileType);
    if (!preview) return;
    const { key, load } = preview;
    await (load ? this.startPreviewLoad(load) : this.inFlightLoads.get(key));
  }

  /** Opens a distinct preview tab for every previewable input and starts their loads together. */
  static async viewDocuments(options: DocumentViewerOptions[]): Promise<void> {
    const seen = new Set<PreviewTabKey>();
    const loads: PreviewLoad[] = [];
    const existingLoads: Promise<void>[] = [];
    const externalFlows: Promise<void>[] = [];
    let firstPreviewKey: PreviewTabKey | null = null;
    let previewTabCount = 0;

    for (const option of options) {
      this.closePreOpenedTab(option.preOpenedTab);
      const fileType = this.getFileType(option.fileName, option.fileType, option.filePath);
      const key = createDocumentPreviewKey(this.previewInput(option, fileType));
      if (seen.has(key)) continue;
      seen.add(key);

      if (fileType === 'audio' || fileType === 'unknown') {
        // Audio transcription and download fallbacks do not create tabs, so their external
        // flows intentionally remain available after the preview-tab quota is exhausted.
        externalFlows.push(this.handleNonPreviewableDocument(option, fileType));
        continue;
      }

      if (previewTabCount >= PREVIEW_TAB_MAX) continue;
      previewTabCount += 1;

      const preview = this.openPreview(option, fileType);
      if (!preview) continue;
      firstPreviewKey ??= preview.key;
      const { load } = preview;
      if (load) {
        loads.push(load);
      } else {
        const inFlight = this.inFlightLoads.get(key);
        if (inFlight) existingLoads.push(inFlight);
      }
    }

    if (firstPreviewKey) {
      usePreviewPanelStore.getState().activateTab(firstPreviewKey);
    }

    await Promise.all([
      ...loads.map((load) => this.startPreviewLoad(load)),
      ...existingLoads,
      ...externalFlows,
    ]);
  }

  /**
   * Prefetch a document on hover/mouseenter so the URL is cached by the time the user clicks.
   * DOCX/DOC: blob download requires caseId (not available here) — no prefetch for these types.
   */
  static prefetchDocument(filePath: string, fileName: string, fileType?: string): void {
    const actualType = this.getFileType(fileName, fileType, filePath);
    if (actualType === 'docx' || actualType === 'doc') return;
    if (this.getCachedUrl(filePath)) return;
    // No other types currently benefit from prefetch
  }

  /**
   * Utility method to show document info
   */
  static getDocumentInfo(fileName: string, fileType?: string): { type: string; canPreview: boolean; icon: string } {
    const actualType = this.getFileType(fileName, fileType);
    
    const info: Record<string, { type: string; canPreview: boolean; icon: string }> = {
      pdf: { type: 'PDF', canPreview: true, icon: '📄' },
      docx: { type: 'Word Document', canPreview: true, icon: '📝' },
      doc: { type: 'Word Document', canPreview: true, icon: '📝' },
      image: { type: 'Image', canPreview: true, icon: '🖼️' },
      eml: { type: 'Email', canPreview: true, icon: '📧' },
      msg: { type: 'Email Outlook', canPreview: true, icon: '📧' },
      csv: { type: 'CSV', canPreview: true, icon: '📊' },
      spreadsheet: { type: 'Tableur', canPreview: true, icon: '📊' },
      unknown: { type: 'Document', canPreview: false, icon: '📎' }
    };

    // CAST: guarded above — 'unknown' key is always present in info
    return info[actualType] ?? info['unknown']!;
  }
}
